
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { Fragment, useState } from 'react';
import {
  AnimationGroup,
  AnimatedItem,
  Tile,
  Text,
  Size,
  Shade,
  Margin,
  Padding,
  Flex,
  FlexItem,
  FlexSpacer,
  Speed,
  Button,
  Direction,
} from '@drawbotics/react-drylus';
import Playground from '~/components/Playground';
export const Item = ({
  width,
  small
}) => {
  return <div style={{
    width
  }}>
      <Tile noPadding mdxType="Tile">
        <img src={small ? 'https://thumbs.werkaandemuur.nl/1/28f0cea7dd6469c398da2500ccd4e936/817x600/thumbnail/fit.jpg' : 'https://www.brusselstimes.com/wp-content/uploads/2019/07/newtowertallestbrusselsrueloiperspectivebrussels.jpg'} style={{
        width: '100%',
        height: small ? 80 : 200,
        objectFit: 'cover'
      }} />
        <Padding size={small ? Size.EXTRA_SMALL : Size.SMALL} mdxType="Padding">
          <Margin size={{
          bottom: Size.EXTRA_SMALL
        }} mdxType="Margin">
            <Text mdxType="Text">A cool project</Text>
          </Margin>
          <Text size={Size.SMALL} shade={Shade.MEDIUM} mdxType="Text">40% sold</Text>
        </Padding>
      </Tile>
    </div>;
};
export const AnimateExitExample = ({
  children
}) => {
  const [isVisible, setIsVisible] = useState(false);
  return children({
    isVisible,
    toggle: () => setIsVisible(!isVisible)
  });
};

const layoutProps = {
  Item,
AnimateExitExample
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Animated Item`}</h1>
<p>{`Used in conjunction with `}<inlineCode parentName="p">{`AnimationGroup`}</inlineCode>{` (see below) this component allows you to add animations to the UI easily. Using `}<inlineCode parentName="p">{`framer-motion`}</inlineCode>{` under the hood most of the animation should work automatically. There are a couple of props available to customize the animation to better suit the interface.`}</p>
<p>{`For common patterns you can see the examples here: `}<a parentName="p" {...{
        "href": "/component-kit/animation"
      }}>{`animation patterns`}</a>{`.`}</p>
<p><strong parentName="p">{`Usage`}</strong></p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { AnimatedItem } from '@drawbotics/react-drylus';
`}</code></pre>

<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<div style={{ width: 300 }}>
  <AnimatedItem>
    <Item />
  </AnimatedItem>
</div>`} component={AnimatedItem} mdxType="Playground">
  <div style={{
        width: 300
      }}>
    <AnimatedItem mdxType="AnimatedItem">
      <Item mdxType="Item" />
    </AnimatedItem>
  </div>
</Playground>
<p><strong parentName="p">{`NOTE`}</strong>{`: the `}<inlineCode parentName="p">{`AnimatedItem`}</inlineCode>{` will take up as much space as it needs, as such, if the item you want to animate takes only a certain width, either set that fixed width to the `}<inlineCode parentName="p">{`AnimatedItem`}</inlineCode>{` itself, or set the `}<inlineCode parentName="p">{`AnimatedItem`}</inlineCode>{` to `}<inlineCode parentName="p">{`display: inline-block`}</inlineCode>{` or equivalent. This will avoid visual distortion where the children of `}<inlineCode parentName="p">{`AnimatedItem`}</inlineCode>{` look like they're coming from the wrong place. The examples below have very low speed, to highlight this problem:`}</p>
<Playground __position={1} __code={`<AnimatedItem transition={{ type: 'tween', duration: 5 }}><Item width={300} /></AnimatedItem>
<Margin size={{ top: Size.LARGE }}>
  <AnimatedItem transition={{ type: 'tween', duration: 5 }} style={{ width: 300 }}><Item /></AnimatedItem>
</Margin>`} mdxType="Playground">
  <AnimatedItem transition={{
        type: 'tween',
        duration: 5
      }} mdxType="AnimatedItem"><Item width={300} mdxType="Item" /></AnimatedItem>
  <Margin size={{
        top: Size.LARGE
      }} mdxType="Margin">
    <AnimatedItem transition={{
          type: 'tween',
          duration: 5
        }} style={{
          width: 300
        }} mdxType="AnimatedItem"><Item mdxType="Item" /></AnimatedItem>
  </Margin>
</Playground>
<h3>{`Animation Speed`}</h3>
<p>{`When we talk about `}<inlineCode parentName="p">{`speed`}</inlineCode>{` here we're not exactly modifying the `}<em parentName="p">{`amount of time`}</em>{` it takes for the animation to complete. Rather, we're tweaking the settings of the spring animation by modifying the mass, damping and stiffness to achieve a more relaxed, or stiffer animation. You can see the difference between `}<inlineCode parentName="p">{`FAST`}</inlineCode>{`, `}<inlineCode parentName="p">{`DEFAULT`}</inlineCode>{`, and `}<inlineCode parentName="p">{`SLOW`}</inlineCode>{` below.`}</p>
<Playground __position={2} __code={`<Flex>
  <FlexItem>
    <AnimatedItem speed={Speed.FAST}><Item /></AnimatedItem>
  </FlexItem>
  <FlexSpacer size={Size.DEFAULT} />
  <FlexItem>
    <AnimatedItem><Item /></AnimatedItem>
  </FlexItem>
  <FlexSpacer size={Size.DEFAULT} />
  <FlexItem>
    <AnimatedItem speed={Speed.SLOW}><Item /></AnimatedItem>
  </FlexItem>
</Flex>`} mdxType="Playground">
  <Flex mdxType="Flex">
    <FlexItem mdxType="FlexItem">
      <AnimatedItem speed={Speed.FAST} mdxType="AnimatedItem"><Item mdxType="Item" /></AnimatedItem>
    </FlexItem>
    <FlexSpacer size={Size.DEFAULT} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <AnimatedItem mdxType="AnimatedItem"><Item mdxType="Item" /></AnimatedItem>
    </FlexItem>
    <FlexSpacer size={Size.DEFAULT} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <AnimatedItem speed={Speed.SLOW} mdxType="AnimatedItem"><Item mdxType="Item" /></AnimatedItem>
    </FlexItem>
  </Flex>
</Playground>
<h1>{`Animation Group`}</h1>
<p>{`This component is complementarly to the `}<inlineCode parentName="p">{`AnimatedItem`}</inlineCode>{` component, in that it allows for 2 things:`}</p>
<ol>
<li parentName="ol">{`By wrapping the items in this component, you can choose whether the `}<inlineCode parentName="li">{`AnimatedItem`}</inlineCode>{` should also animate on exit (this is not possible otherwise)`}</li>
<li parentName="ol">{`You can use it to automatically delay (stagger) each `}<inlineCode parentName="li">{`AnimatedItem`}</inlineCode>{` within it. This is useful with lists, or menu elements arranged in a row.`}</li>
</ol>
{/* TODO rework the animateExit logic */}
<p>{`Note that the component's `}<inlineCode parentName="p">{`animateExit`}</inlineCode>{` option will be applied to `}<strong parentName="p">{`all`}</strong>{` `}<inlineCode parentName="p">{`AnimatedItem`}</inlineCode>{` components down the component tree.`}</p>
<p>{`You'll often already have the option to animate children through the `}<inlineCode parentName="p">{`Flex`}</inlineCode>{` layout component, or the `}<inlineCode parentName="p">{`Grid`}</inlineCode>{`, so this component shouldn't be used for the same purpose, so see if you can achieve what you need with the two components just mentioned before wrapping everything with this component.`}</p>
<p><strong parentName="p">{`Usage`}</strong></p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { AnimationGroup } from '@drawbotics/react-drylus';
`}</code></pre>
<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={3} __code={`<AnimationGroup>
  <AnimatedItem style={{ width: 300 }}><Item small /></AnimatedItem>
  <AnimatedItem style={{ width: 300 }}>
    <Margin size={{ top: Size.DEFAULT }}><Item small /></Margin>
  </AnimatedItem>
  <AnimatedItem style={{ width: 300 }}>
    <Margin size={{ top: Size.DEFAULT }}><Item small /> </Margin>
  </AnimatedItem>
</AnimationGroup>`} component={AnimationGroup} mdxType="Playground">
  <AnimationGroup mdxType="AnimationGroup">
    <AnimatedItem style={{
          width: 300
        }} mdxType="AnimatedItem"><Item small mdxType="Item" /></AnimatedItem>
    <AnimatedItem style={{
          width: 300
        }} mdxType="AnimatedItem">
      <Margin size={{
            top: Size.DEFAULT
          }} mdxType="Margin"><Item small mdxType="Item" /></Margin>
    </AnimatedItem>
    <AnimatedItem style={{
          width: 300
        }} mdxType="AnimatedItem">
      <Margin size={{
            top: Size.DEFAULT
          }} mdxType="Margin"><Item small mdxType="Item" /> </Margin>
    </AnimatedItem>
  </AnimationGroup>
</Playground>

<h3>{`Animate Exit example`}</h3>
<p>{`Internally the `}<inlineCode parentName="p">{`AnimationGroup`}</inlineCode>{` uses `}<inlineCode parentName="p">{`AnimatePresence`}</inlineCode>{` from framer-motion when the `}<inlineCode parentName="p">{`animateExit`}</inlineCode>{` prop is given, that's why the component should be placed outside of the content that is currently being toggled (enter/exit).`}</p>
<p>{`This pattern can be used when you want to toggle between different items and want to animate the unmount of each one, or can be used to animate content between route changes.`}</p>
<Playground __position={4} __code={`<AnimateExitExample>
  {({ isVisible, toggle }) => (
    <Fragment>
      <Margin size={{ bottom: Size.LARGE }}>  
        <Button onClick={toggle}>Toggle items</Button>
      </Margin>
      <AnimationGroup direction={Direction.BOTTOM_UP} animateExit staggerChildren>
        {isVisible ? (
          <Fragment>
            <AnimatedItem style={{ width: 300 }}><Item small /></AnimatedItem>
            <AnimatedItem style={{ width: 300 }}>
              <Margin size={{ top: Size.DEFAULT }}><Item small /></Margin>
            </AnimatedItem>
            <AnimatedItem style={{ width: 300 }}>
              <Margin size={{ top: Size.DEFAULT }}><Item small /> </Margin>
            </AnimatedItem>
          </Fragment>
        ) : null}
      </AnimationGroup>
    </Fragment>
  )}
</AnimateExitExample>`} mode="jsx" mdxType="Playground">
  <AnimateExitExample mdxType="AnimateExitExample">
    {({
          isVisible,
          toggle
        }) => <Fragment mdxType="Fragment">
        <Margin size={{
            bottom: Size.LARGE
          }} mdxType="Margin">  
          <Button onClick={toggle} mdxType="Button">Toggle items</Button>
        </Margin>
        <AnimationGroup direction={Direction.BOTTOM_UP} animateExit staggerChildren mdxType="AnimationGroup">
          {isVisible ? <Fragment mdxType="Fragment">
              <AnimatedItem style={{
                width: 300
              }} mdxType="AnimatedItem"><Item small mdxType="Item" /></AnimatedItem>
              <AnimatedItem style={{
                width: 300
              }} mdxType="AnimatedItem">
                <Margin size={{
                  top: Size.DEFAULT
                }} mdxType="Margin"><Item small mdxType="Item" /></Margin>
              </AnimatedItem>
              <AnimatedItem style={{
                width: 300
              }} mdxType="AnimatedItem">
                <Margin size={{
                  top: Size.DEFAULT
                }} mdxType="Margin"><Item small mdxType="Item" /> </Margin>
              </AnimatedItem>
            </Fragment> : null}
        </AnimationGroup>
      </Fragment>}
  </AnimateExitExample>
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;